import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';

import { LockedFeatureUpsellModal } from '../components/locked-feature-upsell-modal';
import type { RootState } from '../../store/root-reducer';

import { getLockedFeatureUpsellCopyForPlan } from './locked-feature-upsell-registry';
import type { LockedFeatureUpsellRegistryKey } from './locked-feature-upsell-registry';

type LockedFeatureUpsellContextValue = {
  /** Open the shared Growth upsell for a gated meta feature (see `LOCKED_FEATURE_UPSELL`). */
  openLockedFeatureUpsell: (key: LockedFeatureUpsellRegistryKey) => void;
  closeLockedFeatureUpsell: () => void;
};

const LockedFeatureUpsellContext =
  createContext<LockedFeatureUpsellContextValue | null>(null);

export const LockedFeatureUpsellProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const history = useHistory();
  const [activeKey, setActiveKey] =
    useState<LockedFeatureUpsellRegistryKey | null>(null);

  const planCode = useSelector(
    (state: RootState) => state.home.subscription?.planCode,
  );

  const openLockedFeatureUpsell = useCallback((key: LockedFeatureUpsellRegistryKey) => {
    setActiveKey(key);
  }, []);

  const closeLockedFeatureUpsell = useCallback(() => {
    setActiveKey(null);
  }, []);

  /** Plan-specific upgrade CTA label (Free vs Starter). */
  const primaryLabel = useMemo(() => {
    if (!activeKey) {
      return '';
    }

    return getLockedFeatureUpsellCopyForPlan(planCode).upgradeButtonLabel;
  }, [activeKey, planCode]);

  const handlePrimary = useCallback(() => {
    history.push('/billings/upgrade');
    setActiveKey(null);
  }, [history]);

  const value = useMemo(
    () => ({
      openLockedFeatureUpsell,
      closeLockedFeatureUpsell,
    }),
    [closeLockedFeatureUpsell, openLockedFeatureUpsell],
  );

  const show = activeKey !== null;

  return (
    <LockedFeatureUpsellContext.Provider value={value}>
      {children}
      {show && activeKey ? (
        <LockedFeatureUpsellModal
          show={show}
          definition={getLockedFeatureUpsellCopyForPlan(planCode)}
          onClose={closeLockedFeatureUpsell}
          onPrimaryAction={handlePrimary}
          primaryLabel={primaryLabel}
        />
      ) : null}
    </LockedFeatureUpsellContext.Provider>
  );
};

export function useLockedFeatureUpsell(): LockedFeatureUpsellContextValue {
  const ctx = useContext(LockedFeatureUpsellContext);

  if (!ctx) {
    throw new Error(
      'useLockedFeatureUpsell must be used within LockedFeatureUpsellProvider (see root.tsx).',
    );
  }

  return ctx;
}
